import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { generateAnexoPDF } from './utils/generateAnexoPDF';

/**
 * AnexoPreview — view-only inline PDF preview of the Anexo de Aduanas.
 *
 * View mode: calls generateAnexoPDF(..., { returnBytes: true }), wraps bytes
 * in a Blob + object URL, and renders an <iframe> pointing at the URL. What
 * the user sees is byte-identical to the downloaded PDF.
 *
 * Future "edit" mode (not implemented): a form-bound payload would let staff
 * adjust fields (recipient name, item descriptions, quantities) before
 * generating the bytes. The form would call a payload-mutating handler that
 * re-runs the generator on each change. The iframe would re-mount with the
 * new blob URL. Only the data layer changes — the render path is the same.
 */
export default function AnexoPreview({ manifestId, mode = 'view' }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!manifestId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    let createdUrl = null;

    const buildPreview = async () => {
      setLoading(true);
      setError(null);
      try {
        const bytes = await generateAnexoPDF(manifestId, supabase, { returnBytes: true });
        if (cancelled) return;
        const blob = new Blob([bytes], { type: 'application/pdf' });
        createdUrl = URL.createObjectURL(blob);
        setPdfUrl(createdUrl);
      } catch (err) {
        if (cancelled) return;
        console.error('[AnexoPreview] Build error:', err);
        setError(err.message || 'Error generando vista previa.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    buildPreview();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [manifestId]);

  if (!manifestId) return null;

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '12px' }}>
        Generando vista previa del Anexo…
      </div>
    );
  }

  if (error) {
    return (
      <div className="badge badge-hold" style={{ padding: '12px', fontSize: '12px', textAlign: 'center' }}>
        ⚠️ {error}
      </div>
    );
  }

  if (!pdfUrl) return null;

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
      background: 'var(--bg)',
    }}>
      <div style={{
        padding: '8px 12px',
        background: 'var(--blue-soft)',
        color: 'var(--blue-text)',
        fontSize: '11px',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span>📄 Vista previa del Anexo — solo lectura</span>
        <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{mode === 'view' ? 'Modo lectura' : mode}</span>
      </div>
      <iframe
        title="Anexo Preview"
        src={pdfUrl}
        style={{ width: '100%', height: '720px', border: 'none', display: 'block' }}
      />
    </div>
  );
}
